import { zodResolver } from '@hookform/resolvers/zod';
import {
  useInfoPanelDetails,
  useUpdateChannelNameMutation,
} from 'gcs-common/clients/api/entities/channels/channel-api';
import { toast } from 'gcs-common/design-system/hooks/use-toast';
import { Button, ButtonVariant } from 'gcs-common/design-system/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'gcs-common/design-system/ui/dialog';
import { Form, FormControl, FormField, FormItem } from 'gcs-common/design-system/ui/form';
import FormTextField from 'gcs-common/design-system/ui/form-text-field';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';

const FormSchema = z.object({
  channelName: z.string().min(1, { message: 'Erforderlich' }),
});

interface UpdateChannelNameModal {
  isOpen: boolean;
  onRequestClose: () => void;
}

const UpdateChannelNameModal = (props: UpdateChannelNameModal) => {
  const { isOpen, onRequestClose } = props;
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();
  const { data: channelData } = useInfoPanelDetails(selectedChannelId);
  const { mutateAsync, isPending } = useUpdateChannelNameMutation();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      channelName: channelData?.channelName || '',
    },
  });

  useEffect(() => {
    form.reset({
      channelName: channelData?.channelName || '',
    });
  }, [channelData]);

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    await mutateAsync({
      path: {
        channel_id: selectedChannelId,
      },
      body: {
        channel_name: data.channelName,
      },
    }).then(() => {
      toast({
        description: 'Chatname erfolgreich aktualisiert',
        duration: 5000,
        variant: 'success',
      });
      handleClose();
    });
  };

  const handleClose = () => {
    if (isPending) return;
    form.reset();
    onRequestClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Chat umbenennen</DialogTitle>
          <DialogDescription>Wenn Sie den Namen eines Chats ändern, wird er für alle geändert.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="channelName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FormTextField {...field} autoFocus placeholder="Channel Name" />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button variant={ButtonVariant.soft_neutral} onClick={handleClose}>
                Abbrechen
              </Button>
              <Button
                variant={ButtonVariant.solid_accent}
                isLoading={isPending}
                type="submit"
                disabled={isPending || !form.formState.isDirty || !form.formState.isValid}
              >
                Umbenennen
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateChannelNameModal;
